import {
  faAngleDown,
  faAngleRight,
  faAngleUp,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { i18n } from "../../App";
import DateFormat from "../../components/DateFormat";

const News = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDate, setShowDate] = useState(false);
  const [championships, setChampionships] = useState<
    { name: string; id: number }[]
  >([]);
  const [news, setNews] = useState<
    {
      title: string;
      id: number;
      image: string;
      championship: string;
      date: string;
      caption: string;
    }[]
  >([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<(string | number)[]>([]);

  const [championship, setChampionship] = useState("");
  const [searchText, setSearchText] = useState("");
  const [reportOnly, setReportOnly] = useState(false);
  const [dates, setDates] = useState<any[]>([null, null]);

  const [search, setSearch] = useState(false);

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/news/getLatestNews`,
        {
          params: {
            language: i18n.language,
            page,
            dates,
            reportOnly,
            championship,
            searchText,
          },
        }
      )
      .then(({ data }) => {
        setChampionships(data.champs);
        setTotalPages(data.totalPages);
        setNews(data.news);
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(({ response }) => {
        toast.error(response.data.message);
        throw new Error(response);
      });
  }, [page, i18n.language, search]);

  useEffect(() => {
    setPagination(getPagination(page, totalPages));
  }, [page, totalPages]);

  function getPagination(current: number, total: number) {
    const pages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (current > 3) {
      pages.push("...");
    }

    for (let i = current - 1; i <= current + 1; i++) {
      if (i > 1 && i < total) {
        pages.push(i);
      }
    }

    if (current < total - 2) {
      pages.push("...");
    }

    pages.push(total);
    return pages;
  }

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <div id="news-search">
          <input
            className="form-control"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(!search);
                setShowDate(false);
              }
            }}
          />
          <FontAwesomeIcon
            icon={faSearch}
            onClick={() => {
              setSearch(!search);
              setShowDate(false);
            }}
          />
          <FontAwesomeIcon
            icon={showDate ? faAngleUp : faAngleDown}
            onClick={() => setShowDate(!showDate)}
          />
          {showDate && (
            <div id="datesearch">
              <div>
                <label>{t("Championship")}</label>
                <select
                  className="form-control"
                  name="championship"
                  value={championship}
                  required
                  onChange={(e) => {
                    setChampionship(e.target.value);
                  }}
                >
                  <option value={""}>{t("All")}</option>
                  {championships.map((champ) => {
                    return (
                      <option value={champ.name} key={champ.id}>
                        {champ.name}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={reportOnly}
                  onChange={() => setReportOnly(!reportOnly)}
                />
                <label>{t("Reports Only")}</label>
              </div>
              <div>
                <label>{t("Date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={dates[0]}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setDates(([_, second]) => [newDate, second]);
                  }}
                />
                <FontAwesomeIcon icon={faAngleRight} />
                <input
                  type="date"
                  className="form-control"
                  value={dates[1]}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setDates(([first, _]) => [first, newDate]);
                  }}
                />
              </div>
              <div className="search-div">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDate(false)}
                >
                  {t("Close")}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setSearch(!search);
                    setShowDate(false);
                  }}
                >
                  {t("Search")}
                </button>
              </div>
            </div>
          )}
        </div>
        <div id="searchnews-container">
          {news.length === 0 ? (
            <p className="nonewstxt">{t("NoNews")}</p>
          ) : (
            news.map((new_) => {
              return (
                <div
                  key={new_.id}
                  className="new-list-item"
                  onClick={() => {
                    navigate(`${t("routes:News")}/LVF1/${new_.id}`);
                  }}
                >
                  <div>
                    <img src={new_.image} />
                  </div>
                  <div>
                    <DateFormat
                      date={new_.date}
                      format={"short_year"}
                      disaggregated={true}
                    />
                    <b>{new_.title}</b>
                    <i>{new_.caption}</i>
                  </div>
                </div>
              );
            })
          )}
          <div className="pagination">
            {pagination.map((p, i) =>
              p === "..." ? (
                <span key={`ellipsis-${i}`}>...</span>
              ) : (
                <button
                  className="btn"
                  key={`page-${i}`}
                  onClick={() => {
                    setPage(parseInt(p.toString()));
                  }}
                  disabled={p === page}
                >
                  {p}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;
